import type { AcademicSession, AppState } from './types'

export interface CreatePeriodInput {
  courseId: string
  name?: string
  label: string
  weeks: number
  sessionsPerWeek: number
  roster: 'copy' | 'empty'
  sourcePeriodId?: string
}
export interface UpdatePeriodInput {
  periodId: string
  name: string
  label: string
  weeks: number
  sessionsPerWeek: number
  spreadsheetId: string
  tabName: string
  status?: 'ACTIVE'
}
export const chronologicalSessions = (sessions: AcademicSession[]) => [...sessions].sort((a, b) => a.week - b.week || a.sessionNumber - b.sessionNumber)
export const activePeriods = (app: AppState) => app.periods.filter(p => p.status === 'ACTIVE')
export const rosterLocked = (app: AppState, periodId: string) => app.sessions.some(s => s.periodId === periodId && (s.status !== 'DRAFT' || s.startedAt || app.attendance.some(a => a.sessionId === s.id)))

export function parseSpreadsheet(value: string): string {
  const trimmed = value.trim()
  if (!trimmed) return ''
  if (/^[A-Za-z0-9_-]{20,200}$/.test(trimmed)) return trimmed
  try {
    const url = new URL(trimmed)
    const match = url.pathname.match(/^\/spreadsheets\/d\/([A-Za-z0-9_-]{20,200})(?:\/|$)/)
    if (url.protocol === 'https:' && url.hostname === 'docs.google.com' && !url.username && !url.password && match) return match[1]
  } catch { /* report a clear input error below */ }
  throw new Error('Introduce un enlace de Google Sheets válido o su Spreadsheet ID.')
}
export function validateTabName(value: string): string {
  const tab = value.trim() || 'Asistencia'
  if (tab.length > 100 || /[[\]:*?/\\]/.test(tab)) throw new Error('La pestaña debe tener hasta 100 caracteres y no contener : \\ / ? * [ ].')
  return tab
}
export function validatePeriod(input: CreatePeriodInput) {
  if (!/^[A-Za-z0-9][A-Za-z0-9_-]{0,39}$/.test(input.courseId) || !/^[A-Z0-9][A-Z0-9_-]{0,29}$/.test(input.label)) throw new Error('Código y periodo: utiliza letras, números, guiones o guiones bajos.')
  if (!Number.isInteger(input.weeks) || input.weeks < 1 || input.weeks > 52 || !Number.isInteger(input.sessionsPerWeek) || input.sessionsPerWeek < 1 || input.sessionsPerWeek > 7) throw new Error('Elige de 1 a 52 semanas y de 1 a 7 sesiones por semana.')
  if (input.name !== undefined && (!input.name.trim() || input.name.length > 200)) throw new Error('Introduce el nombre del curso (máximo 200 caracteres).')
}

const messages: Record<string, string> = {
  FORBIDDEN: 'No tienes permiso para gestionar este curso o periodo.',
  TEACHER_NOT_ALLOWED: 'Profesor no autorizado.',
  COURSE_OR_PERIOD_EXISTS: 'Ya existe ese código de curso o periodo.',
  PERIOD_HAS_OPEN_SESSION: 'Revisa y finaliza las sesiones ACTIVE / REVIEW antes de archivar.',
  PERIOD_ARCHIVED: 'El periodo está archivado y es de solo lectura.',
  CALENDAR_LOCKED: 'Con sesiones generadas no se puede cambiar el periodo ni sus dimensiones.',
  COURSE_LOCKED: 'El nombre del curso está protegido porque ya tiene actividad académica.',
  ROSTER_LOCKED: 'El padrón está protegido porque el semestre ya comenzó.',
  SYNC_TARGET_LOCKED: 'El destino está protegido porque ya existen finalizaciones o trabajos de sincronización.',
  SHEET_ALREADY_ASSIGNED: 'Ese Google Sheet ya pertenece a otro periodo. Crea un Sheet independiente.',
  INVALID_SHEET: 'El Spreadsheet ID o nombre de pestaña no es válido.',
}
export const managementError = (error: unknown) => error instanceof Error ? messages[error.message] || error.message : 'No se pudo guardar. Intenta nuevamente.'
